import { Fragment, useEffect, useState } from "react";
import type { HeadersFunction, MetaFunction } from "@remix-run/node";
import { json } from "@remix-run/node";
import { useLocation } from "@remix-run/react";
import clsx from "clsx";
import { CacheRevalidate2Weeks } from "@tknf/node-header-helpers";
import common from "../data/common.json";
import guide from "../data/guide.json";
import { DisplayText } from "@/components/DisplayText";
import { createOGP } from "@/utilities/ogp";
import { PageContainer } from "@/components/PageContainer";
import { ArticleLayout } from "@/components/ArticleLayout";
import { GuideSection } from "@/components/Guide/Section";
import { GuideParagraph } from "@/components/Guide/Paragraph";
import { GuideSubtitle } from "@/components/Guide/Subtitle";
import { HomeAccess } from "@/components/Home/Access";

// インスタグラムの読込速度の改善
declare global {
  interface Window {
    instgrm?: {
      Embeds: {
        process: () => void;
      };
    };
  }
}

export const headers: HeadersFunction = () => {
  return {
    "Cache-Control": CacheRevalidate2Weeks(),
  };
};

export const meta: MetaFunction = () => {
  return createOGP({
    pageName: "ご利用について",
    path: "/guide",
  });
};

export function loader() {
  return json({ canonical: `${common.meta.url}/guide` });
}

export default function Guide() {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let script: HTMLScriptElement | null = null;
    let timeoutId: NodeJS.Timeout;

    const loadInstagramScript = () => {
      if (script && document.body.contains(script)) {
        document.body.removeChild(script);
      }

      if (window.instgrm) {
        window.instgrm.Embeds.process();
        setIsLoading(false);
        return;
      }

      script = document.createElement('script');
      script.src = '//www.instagram.com/embed.js';
      script.async = true;

      script.onload = () => {
        clearTimeout(timeoutId);
        setIsLoading(false);
        window.instgrm?.Embeds.process();
      };

      script.onerror = () => {
        clearTimeout(timeoutId);
        setHasError(true);
        setIsLoading(false);
      };

      document.body.appendChild(script);
    };

    timeoutId = setTimeout(() => {
      loadInstagramScript();
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      if (script && document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, [location.pathname]);

  // グローバルメニューのページ読み込み時にアクセスセクションにスクロール高さ調整
  useEffect(() => {
    if (window.location.hash === "#access") {
      const element = document.getElementById("access");
      if (element) {
        const headerHeight = 120;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerHeight;

        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });
      }
    }
  }, []);

  return (
    <Fragment>
      <PageContainer>
        <ArticleLayout
          paths={[
            {
              name: "ホーム",
              url: "/",
            },
            {
              name: "ご利用について",
              url: "/guide",
            },
          ]}
        >
          <div className={clsx("flex", "flex-col", "space-y-8")}>
            <DisplayText element="h1" size="extraLarge">
              ご利用について
            </DisplayText>
            <div className={clsx("grid", "grid-cols-1", "gap-12")}>
              <GuideSection title="ご利用ガイド">
                <GuideParagraph>
                  専用利用をご希望の方は、下記のPDFをダウンロードの上、必ずご一読ください。
                  <br />
                  <a
                    href={guide.guide_pdf_url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className={clsx("underline", "text-blue-600", "hover:text-blue-600", "focus:text-blue-600")}
                  >
                    万代島多目的広場 ご利用ガイド
                  </a>
                  <br />
                  <a
                    href={guide.equipment_guide_pdf_url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className={clsx("underline", "text-blue-600", "hover:text-blue-600", "focus:text-blue-600")}
                  >
                    万代島多目的広場 会場備品
                  </a>
                </GuideParagraph>
              </GuideSection>
              <GuideSection title="平面図">
                <GuideParagraph>
                  会場平面図のPDFをダウンロードいただけます。検討の際にご活用ください。
                  <br />
                  <a
                    href={guide.drawing_inside_pdf_url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className={clsx("underline", "text-blue-600", "hover:text-blue-600", "focus:text-blue-600")}
                  >
                    万代島多目的広場 屋内広場案内図
                  </a>
                  <br />
                  <a
                    href={guide.drawing_pdf_url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className={clsx("underline", "text-blue-600", "hover:text-blue-600", "focus:text-blue-600")}
                  >
                    万代島多目的広場 平面図
                  </a>
                </GuideParagraph>
              </GuideSection>
              <GuideSection title="利用時間">
                <GuideParagraph>9:00 ~ 21:00</GuideParagraph>
              </GuideSection>
              <GuideSection title="休館日">
                <GuideParagraph>なし（設備等点検のため臨時休館する場合があります）</GuideParagraph>
              </GuideSection>
              <GuideSection title="通常時のご案内">
                <GuideParagraph>
                  万代島多目的広場は、通常は一般開放しています。自由に出入りや通行ができますので、港の風景を楽しみながら、休憩や飲食、遊びなど、日常の憩いの場としてご利用ください。
                </GuideParagraph>

                <span className="spacer mt-4" />

                <div className={clsx("flex", "flex-col", "space-y-4")}>
                  {guide.basic_rules.map((item) => {
                    return (
                      <div className="min-w-0" key={item.title}>
                        <div className={clsx("space-y-2")}>
                          <h3 className={clsx("font-bold", "text-gray-800")}>{item.title}</h3>
                          <ul className="text-gray-700">
                            {item.list.map((item) => {
                              return (
                                <li key={item} className={clsx("text-sm", "list-inside", "list-disc")}>
                                  {item}
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </GuideSection>
              <GuideSection title="アクセス" id="access">
                <HomeAccess />
                <div className={clsx("grid", "grid-cols-1", "gap-8", "mt-8", "lg:grid-cols-2")}>
                  <div className="col-span-1">
                    {isLoading && (
                      <div className="flex items-center justify-center h-[400px] bg-gray-100 rounded-lg">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                      </div>
                    )}
                    {hasError ? (
                      <div className="flex items-center justify-center h-[400px] bg-gray-100 rounded-lg">
                        <p className="text-gray-500">インスタグラムの埋め込みの読み込みに失敗しました</p>
                      </div>
                    ) : (
                      <blockquote
                        className="instagram-media"
                        data-instgrm-permalink="https://www.instagram.com/reel/C83_O9QPCbp/"
                        data-instgrm-version="14"
                        style={{
                          background: '#FFF',
                          border: '0',
                          borderRadius: '3px',
                          boxShadow: '0 0 1px 0 rgba(0,0,0,0.5),0 1px 10px 0 rgba(0,0,0,0.15)',
                          margin: '1px',
                          maxWidth: '100%',
                          minWidth: '326px',
                          padding: '0',
                          width: '100%',
                          display: isLoading ? 'none' : 'block'
                        }}
                      >
                      </blockquote>
                    )}
                  </div>
                  <div className="col-span-1">
                    <GuideSubtitle>【車】</GuideSubtitle>
                    <GuideParagraph>
                      {"磐越自動車道　新潟中央インターチェンジより約25分"}
                      <br />
                      {"新潟バイパス　紫竹山インターチェンジより約20分"}
                    </GuideParagraph>
                    <br />
                    <GuideSubtitle>【バス】</GuideSubtitle>
                    <GuideParagraph>
                      {"新潟駅バスターミナル　17番線「朱鷺メッセ・佐渡汽船線」乗車、朱鷺メッセバス停にて下車"}
                      <br />
                      {"万代シテイバスセンター　6番線「佐渡汽船線」乗車、朱鷺メッセバス停にて下車"}
                      <br />
                      {"万代シテイ（新潟日報メディアシップ前）　16番「佐渡汽船線」乗車、朱鷺メッセバス停にて下車"}
                    </GuideParagraph>
                  </div>
                </div>
              </GuideSection>
              <GuideSection title="駐車場">
                <GuideParagraph>
                  専用駐車場はありません。
                  <br />
                  お車でお越しの場合は、周辺の万代島駐車場をご利用ください。
                  <br />
                  <a
                    href="https://www.tokimesse.com/visitor/parking/"
                    target="_blank"
                    rel="noreferrer noopener"
                    className={clsx("underline", "text-blue-600", "hover:text-blue-600", "focus:text-blue-600")}
                  >
                    万代島駐車場について
                  </a>
                  <br />
                  <span className="spacer mt-1" />
                  <span className={clsx("text-sm", "block", "mx-2", "leading-tight")}>
                    ※万代島駐車場の料金は、入場から最初の30分は無料、以後30分までごとに100円です。詳しくは、朱鷺メッセホームページをご確認ください。
                  </span>
                </GuideParagraph>
              </GuideSection>
              <GuideSection title="その他">
                <GuideParagraph>屋内広場をご利用の際は、下記設備・サービスがご利用いただけます。</GuideParagraph>
                <GuideSubtitle>【各種設備】</GuideSubtitle>
                <GuideParagraph>手洗い場、トイレ、多目的トイレ、授乳室、大型鏡、休憩コーナー</GuideParagraph>
                <div className={clsx("grid", "grid-cols-2", "gap-4", "py-6", "md:grid-cols-3")}>
                  {guide.images.map((image) => {
                    return (
                      <div key={image.src} className="col-span-1">
                        <img
                          src={`/images/guide/${image.src}`}
                          width={720}
                          height={540}
                          className={clsx("object-cover", "w-full", "rounded")}
                          alt={image.alt}
                          title={image.alt}
                        />
                      </div>
                    );
                  })}
                </div>
                <GuideSubtitle>【公衆無線LANサービス】</GuideSubtitle>
                <GuideParagraph>
                  屋内広場では、公衆無線LANサービス「Niigata City Wi-Fi（ニイガタシティワイファイ）」を提供しています。
                  <br />
                  どなたでも無料でご利用いただけます。ぜひご利用ください。
                  <br />
                  Niigata City Wi-Fiについて、詳しくは下記リンク先をご覧ください。
                  <br />
                  <a
                    href="https://www.city.niigata.lg.jp/shisei/seisaku/it/free_wi-fi/wi-fi.html"
                    target="_blank"
                    rel="noreferrer noopener"
                    className={clsx("underline", "text-blue-600", "hover:text-blue-600", "focus:text-blue-600")}
                  >
                    公衆無線LANサービス「Niigata City Wi-Fi」
                  </a>
                </GuideParagraph>
              </GuideSection>
            </div>
          </div>
        </ArticleLayout>
      </PageContainer>
    </Fragment>
  );
}
